/**
 * S07 — Opportunity Detail Drawer.
 *
 * Slides in from the right (full-height, 640 px wide on desktop). Body
 * scrolls. Footer holds the Quick Actions (submit, share, mark reviewed,
 * decline). Closes on:
 *   - X button
 *   - Esc key
 *   - Backdrop click
 *   - Successful submission
 */
import React, { useEffect } from "react";
import {
  X,
  Send,
  Share2,
  CheckCircle2,
  XCircle,
  FileSignature,
  Sparkles,
  Building2,
  User as UserIcon,
  Calendar,
  Wallet,
  Clock,
  Layers,
  Cpu,
  ShieldCheck,
  ListChecks,
  Target,
} from "lucide-react";
import {
  fmtAud,
  SeverityChip,
  SectionEyebrow,
} from "@/pages/dashboard/components/DashboardPrimitives";
import { cn } from "@/lib/utils";

const STATUS_TONE = {
  new: "bg-tralenex-50 text-tralenex-700",
  endorsed: "bg-gold-300/10 text-gold-600",
  "in-review": "bg-inset text-ink-secondary",
  "in-flight": "bg-opportunity/10 text-opportunity",
  live: "bg-risk-low/10 text-risk-low",
};

export default function OpportunityDetailDrawer({ opportunity, onClose, onSubmitRequest, onPromote }) {
  useEffect(() => {
    if (!opportunity) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [opportunity, onClose]);

  if (!opportunity) return null;
  const o = opportunity;

  return (
    <div className="fixed inset-0 z-50" data-testid="opportunity-detail-drawer" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-ink-primary/40 backdrop-blur-[2px]"
        data-testid="drawer-backdrop"
      />
      {/* Panel */}
      <aside
        className="absolute right-0 top-0 bottom-0 w-full sm:w-[640px] bg-raised shadow-e4 border-l border-border-subtle flex flex-col anim-slide-right"
        style={{ animation: "slide-in-right 220ms ease-out" }}
      >
        <DrawerHeader o={o} onClose={onClose} />
        <DrawerBody o={o} />
        <DrawerFooter
          status={o.status}
          opportunity={o}
          onClose={onClose}
          onSubmitRequest={() => onSubmitRequest(o)}
          onPromote={onPromote ? () => onPromote(o) : undefined}
        />
      </aside>
    </div>
  );
}

function DrawerHeader({ o, onClose }) {
  return (
    <div className="border-b border-border-subtle px-5 pt-4 pb-4" data-testid="drawer-header">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-meta font-mono text-ink-secondary tabular-nums">{o.id}</span>
            <span
              className={cn(
                "inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold tracking-[0.06em] uppercase",
                STATUS_TONE[o.status] || "bg-inset text-ink-secondary",
              )}
              data-testid="drawer-status"
            >
              {o.status}
            </span>
            <SeverityChip severity={o.risk} />
          </div>
          <h2 className="mt-1 text-h2 text-ink-primary" data-testid="drawer-title">
            {o.title}
          </h2>
          <p className="mt-1 text-meta text-ink-secondary">
            {o.department} · {o.businessUnit}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close drawer"
          className="h-8 w-8 inline-flex items-center justify-center rounded-md text-ink-secondary hover:bg-inset transition-colors"
          data-testid="drawer-close"
        >
          <X size={16} />
        </button>
      </div>

      {/* Score strip */}
      <div className="mt-4 grid grid-cols-4 gap-2 text-center">
        <ScoreTile label="ROI" value={o.roiScore} tone="tralenex" />
        <ScoreTile label="Risk" value={o.riskScore} tone={o.risk === "high" ? "high" : o.risk === "med" ? "med" : "low"} />
        <ScoreTile label="Effort" value={o.effortScore} tone="neutral" />
        <ScoreTile label="Priority" value={o.priorityScore} tone="gold" />
      </div>
    </div>
  );
}

function DrawerBody({ o }) {
  // Memoise the three DataGrid row sets so the inline-array refs are
  // stable across renders (avoids forcing DataGrid to remount).
  const roiRows = React.useMemo(
    () => [
      ["Est. annual value", fmtAud(o.estimatedValueAud)],
      ["Est. annual investment", fmtAud(o.estimatedInvestmentAud)],
      ["Payback period", `${o.paybackMonths} months`],
      ["ROI score", `${o.roiScore} / 100`],
      ["Priority score", `${o.priorityScore} / 100`],
    ],
    [o.estimatedValueAud, o.estimatedInvestmentAud, o.paybackMonths, o.roiScore, o.priorityScore],
  );
  const ownershipRows = React.useMemo(
    () => [
      ["Business owner", o.owner],
      ["Department", o.department],
      ["Business unit", o.businessUnit],
    ],
    [o.owner, o.department, o.businessUnit],
  );
  const timelineRows = React.useMemo(
    () => [
      ["Delivery window", `${o.timelineWeeks} weeks`],
      ["Payback", `${o.paybackMonths} months`],
      ["Effort sizing", o.effort === "S" ? "Small" : o.effort === "M" ? "Medium" : "Large"],
    ],
    [o.timelineWeeks, o.paybackMonths, o.effort],
  );

  return (
    <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6" data-testid="drawer-body">
      {/* Executive Summary */}
      <Section icon={Sparkles} title="Executive Summary" eyebrow="Snapshot">
        <p className="text-[15px] text-ink-primary leading-relaxed" data-testid="drawer-exec-summary">
          {o.summary}{" "}
          <span className="text-ink-secondary">
            Owner {o.owner}, business unit {o.businessUnit}, projected annual value{" "}
            <span className="font-semibold tabular-nums text-ink-primary">
              {fmtAud(o.estimatedValueAud)}
            </span>{" "}
            within {o.paybackMonths} months. Recommended approach: {o.recommendedApproach}.
          </span>
        </p>
      </Section>

      {/* Business Problem */}
      <Section icon={Target} title="Business Problem">
        <p className="text-ink-primary leading-relaxed" data-testid="drawer-problem">
          {o.businessProblem}
        </p>
      </Section>

      {/* Proposed Solution */}
      <Section icon={Layers} title="Proposed Solution">
        <p className="text-ink-primary leading-relaxed" data-testid="drawer-solution">
          {o.proposedSolution}
        </p>
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="drawer-tech">
          {(o.recommendedTech || []).map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-inset text-[11px] text-ink-secondary"
            >
              <Cpu size={10} /> {t}
            </span>
          ))}
        </div>
      </Section>

      {/* Expected ROI */}
      <Section icon={Wallet} title="Expected ROI">
        <DataGrid rows={roiRows} />
      </Section>

      {/* Risk Assessment */}
      <Section icon={ShieldCheck} title="Risk Assessment">
        <p className="text-ink-primary leading-relaxed" data-testid="drawer-risk">
          {o.riskAssessment}
        </p>
        <div className="mt-2 inline-flex items-center gap-2 text-meta text-ink-secondary">
          <span>Risk band</span>
          <SeverityChip severity={o.risk} />
          <span className="tabular-nums">· score {o.riskScore}/100</span>
        </div>
      </Section>

      {/* Compliance Considerations */}
      <Section icon={ShieldCheck} title="Compliance Considerations">
        <div className="flex items-center gap-2 mb-2 text-meta">
          <span className="text-ink-secondary">Impact</span>
          <SeverityChip severity={o.complianceImpact} />
        </div>
        <p className="text-ink-primary leading-relaxed" data-testid="drawer-compliance">
          {o.complianceConsiderations}
        </p>
      </Section>

      {/* Required Approvals */}
      <Section icon={FileSignature} title="Required Approvals">
        <ol className="space-y-1.5" data-testid="drawer-approvals">
          {(o.requiredApprovals || []).map((a, i) => (
            <li key={a} className="flex items-center gap-2.5 text-sm">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-tralenex-50 text-tralenex-700 text-[10px] font-bold tabular-nums">
                {i + 1}
              </span>
              <span className="text-ink-primary">{a}</span>
            </li>
          ))}
        </ol>
      </Section>

      {/* Ownership */}
      <Section icon={UserIcon} title="Ownership">
        <DataGrid rows={ownershipRows} />
      </Section>

      {/* Recommended AI Technologies */}
      <Section icon={Cpu} title="Recommended AI Technologies">
        <p className="text-ink-primary">{o.recommendedApproach}</p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {(o.recommendedTech || []).map((t) => (
            <span
              key={t}
              className="inline-flex items-center px-2 py-1 rounded bg-tralenex-50 text-[12px] text-tralenex-900 font-medium"
            >
              {t}
            </span>
          ))}
        </div>
      </Section>

      {/* Timeline */}
      <Section icon={Calendar} title="Estimated Timeline">
        <DataGrid rows={timelineRows} />
      </Section>

      {/* Success Metrics */}
      <Section icon={ListChecks} title="Success Metrics">
        <ul className="space-y-1.5" data-testid="drawer-metrics">
          {(o.successMetrics || []).map((m) => (
            <li key={m} className="flex items-start gap-2 text-sm">
              <CheckCircle2 size={14} className="text-risk-low mt-0.5 shrink-0" />
              <span className="text-ink-primary">{m}</span>
            </li>
          ))}
        </ul>
      </Section>
    </div>
  );
}

function DrawerFooter({ status, opportunity, onClose, onSubmitRequest, onPromote }) {
  const submittable = status === "new" || status === "endorsed" || status === "in-review";
  const promotable = !!onPromote && (status === "endorsed" || opportunity?.demoHero) && status !== "in-flight" && status !== "deployed";
  const alreadyPromoted = status === "in-flight" || status === "deployed";
  return (
    <div className="border-t border-border-subtle px-5 py-3 bg-inset/30 flex items-center gap-2 flex-wrap" data-testid="drawer-footer">
      <button
        type="button"
        onClick={onClose}
        className="h-9 px-3 rounded-md text-sm font-medium text-ink-secondary hover:bg-inset transition-colors"
        data-testid="drawer-action-close"
      >
        Close
      </button>
      <button
        type="button"
        className="h-9 px-3 rounded-md text-sm font-medium text-ink-secondary hover:bg-inset transition-colors inline-flex items-center gap-1.5"
        data-testid="drawer-action-share"
      >
        <Share2 size={13} /> Share
      </button>
      <button
        type="button"
        className="h-9 px-3 rounded-md text-sm font-medium text-ink-secondary hover:bg-inset transition-colors inline-flex items-center gap-1.5"
        data-testid="drawer-action-decline"
      >
        <XCircle size={13} /> Decline
      </button>
      <span className="flex-1" />
      <button
        type="button"
        disabled={!submittable}
        onClick={onSubmitRequest}
        className={cn(
          "h-9 px-3 rounded-md text-sm font-medium inline-flex items-center gap-1.5 transition-colors",
          submittable
            ? "border border-border-subtle bg-raised text-ink-primary hover:bg-inset"
            : "bg-inset text-ink-disabled cursor-not-allowed",
        )}
        data-testid="drawer-action-submit"
      >
        <Send size={13} /> Submit request
      </button>
      {onPromote && (
        <button
          type="button"
          disabled={!promotable}
          onClick={onPromote}
          className={cn(
            "h-9 px-4 rounded-md text-sm font-semibold inline-flex items-center gap-2 transition-colors shadow-e1",
            promotable
              ? "bg-tralenex-600 text-white hover:bg-tralenex-700"
              : alreadyPromoted
                ? "bg-risk-low/15 text-risk-low border border-risk-low/30 cursor-default"
                : "bg-inset text-ink-disabled cursor-not-allowed",
          )}
          data-testid="drawer-action-promote"
          title={alreadyPromoted ? "Already promoted — open Deployment Requests" : "Promote to deployment workflow"}
        >
          <Sparkles size={13} /> {alreadyPromoted ? "Promoted" : "Promote to deployment"}
        </button>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Internal helpers
// ───────────────────────────────────────────────────────────────────────────
const SCORE_TONE = {
  tralenex: "text-tralenex-700",
  low: "text-risk-low",
  med: "text-risk-med",
  high: "text-risk-high",
  neutral: "text-ink-primary",
  gold: "text-gold-600",
};

function ScoreTile({ label, value, tone }) {
  return (
    <div className="rounded-md bg-inset/60 px-2 py-2">
      <p className="text-[9px] font-bold tracking-[0.1em] uppercase text-ink-secondary">{label}</p>
      <p className={cn("mt-0.5 text-lg font-semibold tabular-nums leading-none", SCORE_TONE[tone])}>
        {value}
      </p>
    </div>
  );
}

function Section({ icon: Icon, title, eyebrow, children }) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={13} className="text-tralenex-700" />
        <p className="eyebrow">{eyebrow || title}</p>
      </div>
      {eyebrow && <h3 className="text-h3 text-ink-primary mb-1.5">{title}</h3>}
      {!eyebrow && <h3 className="text-h3 text-ink-primary mb-1.5 sr-only">{title}</h3>}
      <div className="text-sm">{children}</div>
    </section>
  );
}

function DataGrid({ rows }) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
      {rows.map(([k, v]) => (
        <div key={k} className="flex items-baseline gap-3 border-b border-border-subtle/60 py-1">
          <dt className="text-ink-secondary min-w-[140px]">{k}</dt>
          <dd className="text-ink-primary font-medium tabular-nums">{v}</dd>
        </div>
      ))}
    </dl>
  );
}
